/**
 * Widget Dashboard « Activité récente » — branché sur `dashboard.activiteRecente`
 * (Lot H.3, source `audit_log`). Les 5 dernières opérations du domaine, filtrées
 * côté back selon les permissions du rôle (entrées fiscales/commerciales masquées
 * pour les rôles qui n'y ont pas accès).
 *
 * Chaque ligne : icône par type + libellé humain + date RELATIVE (« il y a 12 min »)
 * + auteur. Cliquable vers la fiche si `url_cible` existe (sinon non cliquable).
 */

import { ACTIVITE_RECENTE_LIMITE_DEFAUT } from "@champ-erp/contracts";
import { Card, Skeleton, WidgetHeader } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ChevronRight,
  Grape,
  Leaf,
  ReceiptText,
  Wine,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { formatDateFr } from "../../lib/format-dates.js";
import { trpc } from "../../lib/trpc.js";

// Le transport JSON (client tRPC sans transformer) sérialise `date` en string :
// on type à partir du retour RÉEL du client, pas du contrat (où `date: Date`).
type ActiviteSortie = Awaited<ReturnType<typeof trpc.dashboard.activiteRecente.query>>;
type ActiviteItem = ActiviteSortie[number];

/** Formateur de dates relatives partagé (numeric: "auto" → « hier », « il y a 2 h »). */
const RTF = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });

/**
 * Date d'opération → texte relatif court. Au-delà d'une semaine, on bascule sur
 * la date absolue (plus lisible qu'« il y a 23 jours »). L'entrée peut être une
 * string ISO (transport JSON) ou un Date.
 */
function formatRelatif(input: string | Date): string {
  const date = typeof input === "string" ? new Date(input) : input;
  const diffSecondes = Math.round((date.getTime() - Date.now()) / 1000); // négatif = passé
  const absSecondes = Math.abs(diffSecondes);

  if (absSecondes < 60) return "à l'instant";
  if (absSecondes < 3600) return RTF.format(Math.round(diffSecondes / 60), "minute");
  if (absSecondes < 86400) return RTF.format(Math.round(diffSecondes / 3600), "hour");
  if (absSecondes < 86400 * 7) return RTF.format(Math.round(diffSecondes / 86400), "day");
  return formatDateFr(date);
}

/**
 * Icône par famille d'action (préfixe du code `action`, ex. `vente.facture.emettre`).
 * On teste des mots-clés pour rester robuste aux variations de nommage.
 */
function iconePourAction(action: string): LucideIcon {
  const a = action.toLowerCase();
  if (a.includes("vendange") || a.includes("pesee") || a.includes("bordereau")) return Grape;
  if (
    a.includes("vente") ||
    a.includes("facture") ||
    a.includes("devis") ||
    a.includes("commande") ||
    a.includes("reglement") ||
    a.includes("avoir")
  )
    return ReceiptText;
  if (
    a.includes("parcelle") ||
    a.includes("vignoble") ||
    a.includes("spcv") ||
    a.includes("phyto") ||
    a.includes("travaux")
  )
    return Leaf;
  if (
    a.includes("cave") ||
    a.includes("tirage") ||
    a.includes("degorgement") ||
    a.includes("cuvee") ||
    a.includes("assemblage") ||
    a.includes("habillage") ||
    a.includes("cuve") ||
    a.includes("vin")
  )
    return Wine;
  return Activity;
}

export function WidgetActiviteRecente() {
  // `domaine.read` : socle de tout membre — le back filtre déjà le sensible.
  const { granted, isLoading: permLoading } = useCan("domaine.read");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard.activiteRecente", ACTIVITE_RECENTE_LIMITE_DEFAUT],
    queryFn: () => trpc.dashboard.activiteRecente.query({ limite: ACTIVITE_RECENTE_LIMITE_DEFAUT }),
    enabled: granted,
    retry: false,
  });

  if (permLoading || !granted) return null;

  return (
    <Card as="section" className="p-6">
      <WidgetHeader eyebrow="Suivi" title="Activité récente" />
      {isLoading ? (
        <Skeleton variant="text" lines={5} />
      ) : isError || !data ? (
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Impossible de charger l&apos;activité récente pour l&apos;instant.
        </p>
      ) : (
        <ActiviteContenu items={data} />
      )}
    </Card>
  );
}

function ActiviteContenu({ items }: { items: ActiviteSortie }) {
  // État vide honnête : rien encore tracé → invitation neutre.
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
          <Activity size={26} />
        </div>
        <p className="text-[14px] text-champagne-800">
          Aucune activité récente. Vos opérations apparaîtront ici.
        </p>
      </div>
    );
  }

  return (
    <ul className="-my-1 divide-y divide-champagne-900/5">
      {items.map((item) => (
        <LigneActivite key={item.id} item={item} />
      ))}
    </ul>
  );
}

/** Une opération : icône + libellé + (date relative · auteur). Cliquable si url_cible. */
function LigneActivite({ item }: { item: ActiviteItem }) {
  const Icon = iconePourAction(item.action);
  const relatif = formatRelatif(item.date);
  const auteur = item.auteur_nom ?? "—";

  const contenu = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] text-aubergine-900">{item.libelle}</span>
        <span className="block text-[12px] text-champagne-700">
          {relatif} · {auteur}
        </span>
      </span>
      {item.url_cible && (
        <ChevronRight
          size={16}
          className="shrink-0 text-champagne-400 transition group-hover:translate-x-0.5 group-hover:text-champagne-600"
        />
      )}
    </>
  );

  return <LigneConteneur url={item.url_cible}>{contenu}</LigneConteneur>;
}

/** Conteneur de ligne : <Link> cliquable si url fournie, sinon <div> statique. */
function LigneConteneur({ url, children }: { url: string | null; children: ReactNode }) {
  const base = "group flex min-h-[56px] items-center gap-3 py-2";
  if (url) {
    return (
      <li>
        <Link
          to={url}
          className={`${base} -mx-2 rounded-lg px-2 transition hover:bg-champagne-50/60`}
        >
          {children}
        </Link>
      </li>
    );
  }
  return (
    <li>
      <div className={base}>{children}</div>
    </li>
  );
}
